import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { NavEntry } from './nav-groups';

/**
 * The top bar's navigation: plain links and grouped dropdowns, one component for both portals.
 *
 * A dropdown closes on outside press, Escape, and any navigation, and is lit ("on") while the
 * current page is one of its members so you can still tell where you are.
 */
export function NavMenu({ entries, isActive }: {
  entries: readonly NavEntry[];
  /** Overrides a link's own active test (the retailer "Shop" tab spans several routes). */
  isActive?: (id: string) => boolean | undefined;
}) {
  /*
   * This used to publish the top bar's measured height as `--mk-top-h`, for tables to pin their
   * header under. Nothing reads it any more: a sticky header resolves its offset against its
   * nearest scroll container, not the viewport, so a viewport offset was wrong inside the table
   * card, inside a dialog body and behind an open dialog — it dropped the heading onto the rows.
   * See the note above `.mk-twrap` in `styles.css`. Do not reintroduce the variable without
   * reintroducing a scroll container it is actually measured against.
   */
  return (
    <nav className="mk-nav">
      {entries.map((e) => (e.kind === 'link'
        ? (
          <NavLink
            key={e.id} to={e.to}
            className={({ isActive: own }) => ((isActive?.(e.id) ?? own) ? 'on' : '')}
          >
            {e.label}
          </NavLink>
        )
        : <Group key={e.label} label={e.label} items={e.items} />))}
    </nav>
  );
}

function Group({ label, items }: { label: string; items: Extract<NavEntry, { kind: 'group' }>['items'] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const current = items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent) => { if (!wrap.current?.contains(ev.target as Node)) setOpen(false); };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="mk-navgroup" ref={wrap}>
      <button
        type="button" className={current ? 'on' : ''} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {label}
        <svg
          className={`mk-chev ${open ? 'on' : ''}`} width="9" height="9" viewBox="0 0 10 10" fill="none"
          stroke="currentColor" strokeWidth="1.6" aria-hidden
        >
          <path d="M1.5 3.5 5 7l3.5-3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className="mk-navmenu" role="menu">
          {items.map((i) => (
            <NavLink key={i.id} to={i.to} role="menuitem" className={({ isActive }) => (isActive ? 'on' : '')}>
              {i.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}
